// src/pages/EditarCampanha.js

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios, { BASE_URL } from "../axiosConfig";
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Gift,
  ImagePlus,
  Save,
  Target,
  Trophy,
} from "lucide-react";
import "./EditarCampanha.css";

const getImagemUrl = (img) => {
  if (!img) return "";
  return img.startsWith("http") ? img : `${BASE_URL}/${img.replace(/^\/?/, "")}`;
};

function EditarCampanha() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editando = Boolean(id);

  const [premios, setPremios] = useState([]);
  const [formData, setFormData] = useState({
    titulo: "",
    descricao: "",
    recompensa: "",
    pontos_objetivo: "",
    data_inicio: "",
    data_fim: "",
    tipo_recompensa: "premio",
    premio_id: "",
    pontos_recompensa: "",
  });

  const [imagemAtual, setImagemAtual] = useState(null);
  const [novaImagem, setNovaImagem] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get("/premios")
      .then((res) => setPremios(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Erro ao carregar prêmios:", err));
  }, []);

  useEffect(() => {
    if (!editando) return;

    axios
      .get(`/campanhas/${id}`)
      .then((res) => {
        const {
          titulo,
          descricao,
          recompensa,
          pontos_objetivo,
          data_inicio,
          data_fim,
          imagem,
          tipo_recompensa,
          premio_id,
          pontos_recompensa,
        } = res.data;

        setFormData({
          titulo,
          descricao,
          recompensa: recompensa || "",
          pontos_objetivo: pontos_objetivo || "",
          data_inicio: data_inicio?.substring(0, 10),
          data_fim: data_fim?.substring(0, 10),
          tipo_recompensa: tipo_recompensa || "premio",
          premio_id: premio_id ? String(premio_id) : "",
          pontos_recompensa: pontos_recompensa ? String(pontos_recompensa) : "",
        });

        setImagemAtual(imagem);
      })
      .catch((err) => {
        console.error("Erro ao buscar campanha:", err);
        setErro("Erro ao carregar dados da campanha.");
      });
  }, [id, editando]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (!file) return;

      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      const maxSize = 5 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        setErro("Formato inválido. Use JPG, PNG ou GIF.");
        return;
      }

      if (file.size > maxSize) {
        setErro("Imagem muito grande. O limite é 5MB.");
        return;
      }

      setNovaImagem(file);
      setErro("");
      return;
    }

    if (name === "tipo_recompensa") {
      setFormData((prev) => ({
        ...prev,
        tipo_recompensa: value,
        premio_id: value === "premio" ? prev.premio_id : "",
        pontos_recompensa: value === "pontos" ? prev.pontos_recompensa : "",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setMensagem("");
    setErro("");

    const {
      titulo,
      descricao,
      data_inicio,
      data_fim,
      tipo_recompensa,
      premio_id,
      pontos_recompensa,
    } = formData;

    if (!titulo || !descricao || !data_inicio || !data_fim) {
      setErro("Por favor, preencha todos os campos obrigatórios.");
      setIsSubmitting(false);
      return;
    }

    if (tipo_recompensa === "premio" && !premio_id) {
      setErro("Selecione o prêmio da campanha.");
      setIsSubmitting(false);
      return;
    }

    if (
      tipo_recompensa === "pontos" &&
      (!pontos_recompensa || Number(pontos_recompensa) <= 0)
    ) {
      setErro("Informe a quantidade de pontos da recompensa.");
      setIsSubmitting(false);
      return;
    }

    const formDataToSend = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      formDataToSend.append(key, value);
    });

    if (novaImagem) {
      formDataToSend.append("imagem", novaImagem);
    }

    try {
      if (editando) {
        await axios.put(`/campanhas/${id}`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMensagem("Campanha atualizada com sucesso!");
      } else {
        await axios.post("/campanhas", formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMensagem("Campanha criada com sucesso!");
      }

      setTimeout(() => navigate("/campanhas"), 1500);
    } catch (err) {
      console.error("Erro ao salvar campanha:", err);
      setErro(err.response?.data?.error || "Erro ao salvar campanha.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="editarCampanhaAdmin-page">
      <div className="editarCampanhaAdmin-topbar">
        <div>
          <span className="editarCampanhaAdmin-kicker">
            Campanhas promocionais
          </span>

          <h2>{editando ? "Editar Campanha" : "Nova Campanha"}</h2>

          <p>
            Configure os dados da campanha, período de validade, objetivo,
            recompensa e imagem de divulgação.
          </p>
        </div>

        <button
          type="button"
          className="editarCampanhaAdmin-btn secondary"
          onClick={() => navigate("/campanhas")}
          disabled={isSubmitting}
        >
          <ArrowLeft size={16} />
          Voltar para campanhas
        </button>
      </div>

      {erro && <div className="editarCampanhaAdmin-alert danger">{erro}</div>}

      {mensagem && (
        <div className="editarCampanhaAdmin-alert success">
          <CheckCircle2 size={16} />
          {mensagem}
        </div>
      )}

      <form className="editarCampanhaAdmin-form" onSubmit={handleSubmit}>
        <div className="editarCampanhaAdmin-layout">
          <section className="editarCampanhaAdmin-card">
            <div className="editarCampanhaAdmin-cardHeader">
              <div>
                <h3>Dados da campanha</h3>
                <span>Informações principais da campanha promocional.</span>
              </div>

              <Trophy size={18} />
            </div>

            <div className="editarCampanhaAdmin-grid">
              <div className="editarCampanhaAdmin-field editarCampanhaAdmin-fieldFull">
                <label>Título</label>
                <input
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                />
              </div>

              <div className="editarCampanhaAdmin-field editarCampanhaAdmin-fieldFull">
                <label>Descrição</label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                />
              </div>

              <div className="editarCampanhaAdmin-field">
                <label>Objetivo de pontos</label>
                <input
                  name="pontos_objetivo"
                  type="number"
                  value={formData.pontos_objetivo}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="editarCampanhaAdmin-field">
                <label>Recompensa textual</label>
                <input
                  name="recompensa"
                  value={formData.recompensa}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder="Ex: concorra a um prêmio especial"
                />
              </div>
            </div>
          </section>

          <section className="editarCampanhaAdmin-card">
            <div className="editarCampanhaAdmin-cardHeader">
              <div>
                <h3>Recompensa</h3>
                <span>Defina se a campanha entrega produto ou pontos.</span>
              </div>

              <Gift size={18} />
            </div>

            <div className="editarCampanhaAdmin-grid">
              <div className="editarCampanhaAdmin-field">
                <label>Tipo de recompensa</label>
                <select
                  name="tipo_recompensa"
                  value={formData.tipo_recompensa}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="premio">Produto (Prêmio)</option>
                  <option value="pontos">Pontos</option>
                </select>
              </div>

              {formData.tipo_recompensa === "premio" ? (
                <div className="editarCampanhaAdmin-field">
                  <label>Prêmio da campanha</label>
                  <select
                    name="premio_id"
                    value={formData.premio_id}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Selecione...</option>
                    {premios.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome} —{" "}
                        {Number(p.valor_em_pontos || 0).toLocaleString("pt-BR")} pts
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="editarCampanhaAdmin-field">
                  <label>Pontos da recompensa</label>
                  <input
                    name="pontos_recompensa"
                    type="number"
                    min="1"
                    value={formData.pontos_recompensa}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          </section>

          <section className="editarCampanhaAdmin-card">
            <div className="editarCampanhaAdmin-cardHeader">
              <div>
                <h3>Período</h3>
                <span>Defina quando a campanha estará vigente.</span>
              </div>

              <CalendarDays size={18} />
            </div>

            <div className="editarCampanhaAdmin-grid">
              <div className="editarCampanhaAdmin-field">
                <label>Data de início</label>
                <input
                  type="date"
                  name="data_inicio"
                  value={formData.data_inicio}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                />
              </div>

              <div className="editarCampanhaAdmin-field">
                <label>Data de fim</label>
                <input
                  type="date"
                  name="data_fim"
                  value={formData.data_fim}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>
          </section>

          <aside className="editarCampanhaAdmin-card editarCampanhaAdmin-previewCard">
            <div className="editarCampanhaAdmin-cardHeader">
              <div>
                <h3>Imagem da campanha</h3>
                <span>Atualize ou confira o banner usado na campanha.</span>
              </div>

              <ImagePlus size={18} />
            </div>

            <div className="editarCampanhaAdmin-previewBox">
              {novaImagem ? (
                <img src={URL.createObjectURL(novaImagem)} alt="Nova imagem" />
              ) : imagemAtual ? (
                <img src={getImagemUrl(imagemAtual)} alt="Imagem atual" />
              ) : (
                <div className="editarCampanhaAdmin-previewEmpty">
                  <ImagePlus size={28} />
                  <span>Nenhuma imagem selecionada</span>
                </div>
              )}
            </div>

            <div className="editarCampanhaAdmin-previewInfo">
              <strong>{formData.titulo || "Título da campanha"}</strong>
              <span>{novaImagem ? "Nova imagem selecionada" : "Imagem atual"}</span>
            </div>

            <div className="editarCampanhaAdmin-uploadArea">
              <div className="editarCampanhaAdmin-field">
                <label>Imagem (banner)</label>
                <input
                  type="file"
                  name="imagem"
                  accept="image/*"
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                <small>Apenas JPG, PNG ou GIF — até 5MB.</small>
              </div>
            </div>
          </aside>
        </div>

        <div className="editarCampanhaAdmin-submitBar">
          <button
            type="submit"
            className="editarCampanhaAdmin-btn primary"
            disabled={isSubmitting}
          >
            <Save size={16} />
            {isSubmitting ? "Salvando..." : "Salvar campanha"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditarCampanha;